/**
 * Admin — Member Usage & Activity Report
 *
 * Tenant-aware, lightweight analytics for member engagement.
 * Super-admin only. Five sections: Overview cards, Feature Usage,
 * Member Activity, Login History, Inactive Members. Filterable by
 * date range, category, status and search. CSV export supported.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import AdminLayout from '@/components/AdminLayout';
import {
  Users, Activity, LogIn, UserX, Download, Search, RotateCcw,
  TrendingUp, TrendingDown, CalendarClock, MonitorSmartphone,
} from 'lucide-react';

const STATUS_OPTIONS = ['all', 'active', 'pending', 'suspended', 'inactive'];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const fmtDuration = (secs) => {
  if (secs === null || secs === undefined) return '—';
  const s = Math.max(0, parseInt(secs, 10));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${sec}s`;
  return `${sec}s`;
};

const StatCard = ({ icon: Icon, label, value, sub, testId, accent = 'text-primary' }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-3xl font-heading font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminUsageReport = () => {
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);

  // Data
  const [overview, setOverview] = useState(null);
  const [featureUsage, setFeatureUsage] = useState([]);
  const [activity, setActivity] = useState({ members: [], total: 0, page: 1, total_pages: 1 });
  const [logins, setLogins] = useState({ sessions: [], total: 0, page: 1, total_pages: 1 });
  const [inactive, setInactive] = useState({ group_30: [], group_60: [], group_90: [] });

  const [activityPage, setActivityPage] = useState(1);
  const [loginPage, setLoginPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feature-usage');

  const buildParams = useCallback((extra = {}) => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    if (category && category !== 'all') p.category = category;
    if (status && status !== 'all') p.status = status;
    if (search.trim()) p.search = search.trim();
    return { ...p, ...extra };
  }, [dateFrom, dateTo, category, status, search]);

  const loadCategories = async () => {
    try {
      const res = await apiClient.get('/usage/reports/categories');
      setCategories(res.data || []);
    } catch { /* non-blocking */ }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      const [ov, fu, act, lg, ina] = await Promise.all([
        apiClient.get('/usage/reports/overview', { params }),
        apiClient.get('/usage/reports/feature-usage', { params }),
        apiClient.get('/usage/reports/member-activity', { params: { ...params, page: activityPage, page_size: 20 } }),
        apiClient.get('/usage/reports/login-history', { params: { ...params, page: loginPage, page_size: 20 } }),
        apiClient.get('/usage/reports/inactive', { params }),
      ]);
      setOverview(ov.data);
      setFeatureUsage(fu.data || []);
      setActivity(act.data);
      setLogins(lg.data);
      setInactive(ina.data);
    } catch (error) {
      toast.error('Failed to load usage reports');
    } finally {
      setLoading(false);
    }
  }, [buildParams, activityPage, loginPage]);

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const applyFilters = () => {
    setActivityPage(1);
    setLoginPage(1);
    loadAll();
  };

  const resetFilters = () => {
    setDateFrom(''); setDateTo(''); setCategory('all'); setStatus('all'); setSearch('');
    setActivityPage(1); setLoginPage(1);
  };

  const exportCsv = async (report) => {
    try {
      const params = buildParams({ report });
      const res = await apiClient.get('/usage/reports/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report.replace('-', '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const maxVisits = Math.max(1, ...featureUsage.map((f) => f.total_visits || 0));

  const activityBadge = (s) => (
    <Badge variant="outline" className={s === 'Active'
      ? 'bg-green-100 text-green-700 border-green-200'
      : 'bg-gray-100 text-gray-600 border-gray-200'}>
      {s}
    </Badge>
  );

  const inactiveTable = (rows, testId) => (
    <div className="overflow-x-auto" data-testid={testId}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Member</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Last Login</TableHead>
            <TableHead className="text-right">Days Inactive</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No members in this range</TableCell></TableRow>
          ) : rows.map((m) => (
            <TableRow key={m.member_id}>
              <TableCell className="font-medium">{m.member_name}</TableCell>
              <TableCell className="text-muted-foreground">{m.email || '—'}</TableCell>
              <TableCell>{m.membership_type || '—'}</TableCell>
              <TableCell>{m.last_login ? fmtDateTime(m.last_login) : 'Never'}</TableCell>
              <TableCell className="text-right">{m.days_inactive ?? '90+'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <AdminLayout currentPage="usage" title="Member Usage & Activity">
      <div className="space-y-6" data-testid="usage-report-page">
        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="filter-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s === 'all' ? 'All statuses' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <Input placeholder="Name, email, ID" value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()} data-testid="filter-search" />
              </div>
              <div className="flex gap-2">
                <Button onClick={applyFilters} className="flex-1" data-testid="apply-filters-btn">
                  <Search className="w-4 h-4 mr-2" />Apply
                </Button>
                <Button variant="outline" onClick={resetFilters} data-testid="reset-filters-btn">
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Overview cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={LogIn} label="Logged in today" value={overview?.members_logged_in_today ?? '—'}
            sub={`of ${overview?.total_members ?? 0} members`} testId="card-logged-in-today" />
          <StatCard icon={Activity} label="Active members" value={overview?.active_members ?? '—'}
            sub="in selected period" accent="text-green-600" testId="card-active-members" />
          <StatCard icon={UserX} label="Inactive members" value={overview?.inactive_members ?? '—'}
            sub="no login in 30 days" accent="text-amber-600" testId="card-inactive-members" />
          <StatCard icon={Users} label="Avg sessions / member" value={overview?.average_sessions_per_member ?? '—'}
            sub="active members" accent="text-blue-600" testId="card-avg-sessions" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <StatCard icon={TrendingUp} label="Most used feature" value={overview?.most_used_feature || '—'}
            accent="text-green-600" testId="card-most-used" />
          <StatCard icon={TrendingDown} label="Least used feature" value={overview?.least_used_feature || '—'}
            accent="text-red-500" testId="card-least-used" />
        </div>

        {/* Detail tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4" data-testid="usage-tabs">
            <TabsTrigger value="feature-usage" data-testid="tab-feature-usage">Feature Usage</TabsTrigger>
            <TabsTrigger value="member-activity" data-testid="tab-member-activity">Member Activity</TabsTrigger>
            <TabsTrigger value="login-history" data-testid="tab-login-history">Login History</TabsTrigger>
            <TabsTrigger value="inactive" data-testid="tab-inactive">Inactive Members</TabsTrigger>
          </TabsList>

          {/* Feature Usage */}
          <TabsContent value="feature-usage" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Feature Usage</CardTitle>
                <Button variant="outline" size="sm" onClick={() => exportCsv('feature-usage')} data-testid="export-feature-usage">
                  <Download className="w-4 h-4 mr-2" />Export CSV
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {featureUsage.map((f) => (
                  <div key={f.feature} data-testid={`feature-row-${f.feature}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{f.feature}</span>
                      <span className="text-muted-foreground">{f.total_visits} visits · {f.unique_members} members</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${Math.round((f.total_visits / maxVisits) * 100)}%` }} />
                    </div>
                  </div>
                ))}
                {featureUsage.length === 0 && !loading && (
                  <p className="text-center text-muted-foreground py-6">No usage data for the selected filters.</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Member Activity */}
          <TabsContent value="member-activity" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Member Activity</CardTitle>
                <Button variant="outline" size="sm" onClick={() => exportCsv('member-activity')} data-testid="export-member-activity">
                  <Download className="w-4 h-4 mr-2" />Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead className="text-right">Sessions</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activity.members.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No members found</TableCell></TableRow>
                      ) : activity.members.map((m) => (
                        <TableRow key={m.member_id} data-testid={`activity-row-${m.member_id}`}>
                          <TableCell>
                            <div className="font-medium">{m.member_name}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                          </TableCell>
                          <TableCell>{m.membership_type || '—'}</TableCell>
                          <TableCell>{m.last_login ? fmtDateTime(m.last_login) : 'Never'}</TableCell>
                          <TableCell className="text-right">{m.sessions}</TableCell>
                          <TableCell>{activityBadge(m.activity_status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {activity.page} of {activity.total_pages} · {activity.total} members
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={activity.page <= 1}
                      onClick={() => setActivityPage((p) => p - 1)} data-testid="activity-prev">Previous</Button>
                    <Button variant="outline" size="sm" disabled={activity.page >= activity.total_pages}
                      onClick={() => setActivityPage((p) => p + 1)} data-testid="activity-next">Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Login History */}
          <TabsContent value="login-history" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Login History</CardTitle>
                <Button variant="outline" size="sm" onClick={() => exportCsv('login-history')} data-testid="export-login-history">
                  <Download className="w-4 h-4 mr-2" />Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Member</TableHead>
                        <TableHead>Login</TableHead>
                        <TableHead>Logout</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Device</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logins.sessions.length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No login sessions</TableCell></TableRow>
                      ) : logins.sessions.map((s) => (
                        <TableRow key={s.session_id} data-testid={`login-row-${s.session_id}`}>
                          <TableCell className="font-medium">{s.member_name}</TableCell>
                          <TableCell>{fmtDateTime(s.login_time)}</TableCell>
                          <TableCell>{s.logout_time ? fmtDateTime(s.logout_time) : '—'}</TableCell>
                          <TableCell><CalendarClock className="w-3.5 h-3.5 inline mr-1 text-muted-foreground" />{fmtDuration(s.duration_seconds)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            <MonitorSmartphone className="w-3.5 h-3.5 inline mr-1" />
                            {s.device_type} · {s.browser} · {s.operating_system}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {logins.page} of {logins.total_pages} · {logins.total} sessions
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={logins.page <= 1}
                      onClick={() => setLoginPage((p) => p - 1)} data-testid="login-prev">Previous</Button>
                    <Button variant="outline" size="sm" disabled={logins.page >= logins.total_pages}
                      onClick={() => setLoginPage((p) => p + 1)} data-testid="login-next">Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Inactive Members */}
          <TabsContent value="inactive" className="mt-4 space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2">
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">30–59 days</Badge>
                Inactive ({inactive.group_30.length})
              </CardTitle></CardHeader>
              <CardContent>{inactiveTable(inactive.group_30, 'inactive-30')}</CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2">
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">60–89 days</Badge>
                Inactive ({inactive.group_60.length})
              </CardTitle></CardHeader>
              <CardContent>{inactiveTable(inactive.group_60, 'inactive-60')}</CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2">
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">90+ days / never</Badge>
                Inactive ({inactive.group_90.length})
              </CardTitle></CardHeader>
              <CardContent>{inactiveTable(inactive.group_90, 'inactive-90')}</CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminUsageReport;
